import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe } from 'lucide-react';
import { Button } from './ui/button';

const COLORS = {
  primary: '#1A4D5C',
  gold: '#C89846',
  darkGray: '#2C3E50',
  mediumGray: '#7F8C8D',
  lightGray: '#ECF0F1',
  white: '#FFFFFF'
};

const LOGO_HORIZONTAL = 'https://customer-assets.emergentagent.com/job_hr-tools-upgrade/artifacts/87hf4ifo_Mevia%20Hr%20Color.png';

const PRIVACY_TEXT = {
  es: `AVISO DE PRIVACIDAD INTEGRAL

MEVIA DATA WITH SOUL, S.A.S. (en adelante "MEVIA"), con domicilio en México, es responsable del tratamiento de sus datos personales.

1. DATOS PERSONALES QUE RECABAMOS

Para las finalidades señaladas en el presente Aviso de Privacidad, MEVIA podrá recabar y tratar los siguientes datos personales:

Datos de identificación: nombre completo, fecha de nacimiento, género, CURP, RFC, número de seguridad social, fotografía.
Datos de contacto: correo electrónico, número telefónico, dirección.
Datos laborales: puesto, área, departamento, antigüedad, tipo de contrato, jornada laboral, salario.
Datos académicos: nivel de estudios, institución educativa, cédula profesional.

DATOS PERSONALES SENSIBLES

MEVIA recabará y tratará los siguientes datos personales sensibles:

- Resultados de evaluaciones psicométricas (Eneagrama de la personalidad).
- Resultados de evaluaciones de riesgos psicosociales (NOM-035-STPS-2018).
- Información proporcionada a través del Buzón Anónimo sobre condiciones laborales.
- Resultados de evaluaciones de cultura organizacional.
- Información de seguimiento médico derivada de canalizaciones por eventos traumáticos severos.

2. FINALIDADES DEL TRATAMIENTO

Sus datos personales serán utilizados para las siguientes finalidades primarias (necesarias):
- Gestión del expediente laboral dentro de la plataforma MEVIA.
- Aplicación y procesamiento de evaluaciones psicométricas y organizacionales.
- Cumplimiento de la NOM-035-STPS-2018 (identificación y análisis de factores de riesgo psicosocial).
- Generación de reportes e indicadores de talento humano para la empresa contratante.
- Seguimiento de canalizaciones médicas derivadas de evaluaciones.
- Comunicación de resultados y notificaciones relacionadas con evaluaciones.

Finalidades secundarias (no necesarias):
- Elaboración de estudios estadísticos agregados y anonimizados sobre tendencias de talento.
- Mejora continua de los algoritmos y modelos de evaluación de MEVIA.

Si usted no desea que sus datos personales sean tratados para las finalidades secundarias, puede comunicarlo al correo electrónico: privacidad@mevia.mx

3. TRANSFERENCIAS DE DATOS

MEVIA podrá transferir sus datos personales a:
- La empresa contratante de los servicios de MEVIA (su empleador), exclusivamente los resultados agregados y no individualizados, salvo que usted otorgue consentimiento expreso.
- Autoridades competentes cuando así lo requiera la ley.
- Profesionales de la salud en caso de canalización derivada de la Guía I de la NOM-035.

4. DERECHOS ARCO

Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al tratamiento de sus datos personales (Derechos ARCO). Para ejercer dichos derechos, envíe su solicitud a: privacidad@mevia.mx

Su solicitud deberá contener:
- Nombre completo y medio de contacto para comunicarle la respuesta.
- Documentos que acrediten su identidad.
- Descripción clara y precisa de los datos personales respecto de los cuales busca ejercer alguno de los Derechos ARCO.
- Cualquier elemento o documento que facilite la localización de sus datos personales.

MEVIA responderá en un plazo máximo de 20 días hábiles contados desde la recepción de la solicitud.

5. MECANISMOS PARA REVOCAR CONSENTIMIENTO

Usted puede revocar su consentimiento para el tratamiento de sus datos personales enviando una solicitud a: privacidad@mevia.mx

Importante: La revocación del consentimiento para el tratamiento de datos sensibles impedirá la aplicación de evaluaciones y el acceso a la plataforma MEVIA.

6. CAMBIOS AL AVISO DE PRIVACIDAD

MEVIA se reserva el derecho de efectuar modificaciones al presente Aviso de Privacidad. Cualquier cambio será notificado a través de la plataforma MEVIA y estará disponible en: www.mevia.mx/privacidad

7. CONSENTIMIENTO

Al utilizar la plataforma MEVIA y aceptar este Aviso de Privacidad, usted otorga su consentimiento expreso para el tratamiento de sus datos personales, incluyendo datos personales sensibles, conforme a lo descrito en el presente documento.

Fecha de última actualización: Abril 2026

MEVIA DATA WITH SOUL, S.A.S.
privacidad@mevia.mx`,

  en: `PRIVACY NOTICE

MEVIA DATA WITH SOUL, S.A.S. (hereinafter "MEVIA"), with domicile in Mexico, is responsible for the processing of your personal data.

1. PERSONAL DATA WE COLLECT

For the purposes set forth in this Privacy Notice, MEVIA may collect and process the following personal data:

Identification data: full name, date of birth, gender, CURP, RFC, social security number, photograph.
Contact data: email address, phone number, address.
Employment data: position, area, department, seniority, contract type, work schedule, salary.
Academic data: education level, educational institution, professional license.

SENSITIVE PERSONAL DATA

MEVIA will collect and process the following sensitive personal data:

- Results of psychometric assessments (Enneagram of Personality).
- Results of psychosocial risk assessments (NOM-035-STPS-2018).
- Information provided through the Anonymous Mailbox regarding working conditions.
- Results of organizational culture assessments.
- Medical follow-up information derived from referrals for severe traumatic events.

2. PURPOSES OF PROCESSING

Your personal data will be used for the following primary (necessary) purposes:
- Management of the employment record within the MEVIA platform.
- Application and processing of psychometric and organizational assessments.
- Compliance with NOM-035-STPS-2018 (identification and analysis of psychosocial risk factors).
- Generation of human talent reports and indicators for the contracting company.
- Follow-up of medical referrals derived from assessments.
- Communication of results and notifications related to assessments.

Secondary (non-necessary) purposes:
- Preparation of aggregated and anonymized statistical studies on talent trends.
- Continuous improvement of MEVIA's assessment algorithms and models.

If you do not wish your personal data to be processed for secondary purposes, you may notify us at: privacidad@mevia.mx

3. DATA TRANSFERS

MEVIA may transfer your personal data to:
- The company contracting MEVIA's services (your employer), exclusively aggregated and non-individualized results, unless you provide express consent.
- Competent authorities when required by law.
- Health professionals in case of referral derived from NOM-035 Guide I.

4. ARCO RIGHTS

You have the right to Access, Rectify, Cancel, or Oppose the processing of your personal data (ARCO Rights). To exercise these rights, send your request to: privacidad@mevia.mx

Your request must contain:
- Full name and contact information for communicating the response.
- Documents proving your identity.
- Clear and precise description of the personal data for which you seek to exercise any ARCO Right.
- Any element or document that facilitates the location of your personal data.

MEVIA will respond within a maximum period of 20 business days from receipt of the request.

5. MECHANISMS TO REVOKE CONSENT

You may revoke your consent for the processing of your personal data by sending a request to: privacidad@mevia.mx

Important: Revoking consent for the processing of sensitive data will prevent the application of assessments and access to the MEVIA platform.

6. CHANGES TO THIS PRIVACY NOTICE

MEVIA reserves the right to make modifications to this Privacy Notice. Any changes will be notified through the MEVIA platform and will be available at: www.mevia.mx/privacidad

7. CONSENT

By using the MEVIA platform and accepting this Privacy Notice, you expressly consent to the processing of your personal data, including sensitive personal data, as described in this document.

Last updated: April 2026

MEVIA DATA WITH SOUL, S.A.S.
privacidad@mevia.mx`
};

const PrivacyNoticePage = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(
    navigator.language?.startsWith('en') ? 'en' : 'es'
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.white }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')} 
              className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
              style={{ color: COLORS.primary }}
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {lang === 'es' ? 'Volver al inicio' : 'Back to home'}
            </button>
          </div>
          <div className="flex items-center gap-4">
            <img src={LOGO_HORIZONTAL} alt="MEVIA" className="h-7" />
            <button
              onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
              className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border transition-colors hover:bg-gray-50"
              style={{ borderColor: COLORS.mediumGray, color: COLORS.darkGray }}
              data-testid="privacy-lang-toggle"
            >
              <Globe className="w-4 h-4" />
              {lang === 'es' ? 'EN' : 'ES'}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: COLORS.darkGray }}>
            {lang === 'es' ? 'Aviso de Privacidad' : 'Privacy Notice'}
          </h1>
          <p className="text-sm" style={{ color: COLORS.mediumGray }}>
            MEVIA DATA WITH SOUL, S.A.S.
          </p>
          <div className="mt-4 h-1 w-20 rounded" style={{ backgroundColor: COLORS.gold }} />
        </div>

        <article 
          className="prose prose-gray max-w-none"
          style={{ 
            color: COLORS.darkGray,
            fontSize: '16px',
            lineHeight: '1.8',
            whiteSpace: 'pre-wrap'
          }}
          data-testid="privacy-text-content"
        >
          {PRIVACY_TEXT[lang]}
        </article>

        <div className="mt-12 pt-8 border-t flex items-center justify-between" style={{ borderTopColor: COLORS.lightGray }}>
          <Button 
            variant="outline" 
            onClick={() => navigate('/')}
            className="gap-2"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'es' ? 'Volver al inicio' : 'Back to home'}
          </Button>
          <p className="text-xs" style={{ color: COLORS.mediumGray }}>
            {lang === 'es' ? 'Última actualización: Abril 2026' : 'Last updated: April 2026'}
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8" style={{ backgroundColor: COLORS.darkGray }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <img src={LOGO_HORIZONTAL} alt="MEVIA" className="h-6 brightness-0 invert mx-auto mb-3" />
          <p className="text-sm" style={{ color: COLORS.mediumGray }}>
            © {new Date().getFullYear()} MEVIA DATA WITH SOUL, S.A.S. {lang === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
          </p>
          <p className="text-xs mt-2" style={{ color: COLORS.mediumGray }}>
            privacidad@mevia.mx
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyNoticePage;
